import { useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';

import { useAuthContext } from '../hooks';
import { removeUser, signInRedirect } from '../context';
import { Role } from '../types';

// ----------------------------------------------------------------------

type AuthGuardProps = {
  children: React.ReactNode;
  splashScreen: React.ReactNode;
  loginPath: string;
  pathOnAuthProblem: string;
  allowedRoles?: string[];
  insufficientRolePath?: string;
};

type AuthContainerProps = {
  children: React.ReactNode;
  loginPath: string;
  pathOnAuthProblem: string;
  allowedRoles?: string[];
  insufficientRolePath?: string;
};
// TODO: Verify we need this
export default function AuthGuard({
  children,
  splashScreen,
  loginPath,
  pathOnAuthProblem,
  allowedRoles,
  insufficientRolePath = '/insufficient-role',
}: AuthGuardProps) {
  const { loading } = useAuthContext();

  return (
    <>
      {loading ? (
        splashScreen
      ) : (
        <Container 
          loginPath={loginPath} 
          pathOnAuthProblem={pathOnAuthProblem}
          allowedRoles={allowedRoles}
          insufficientRolePath={insufficientRolePath}
        >
          {children}
        </Container>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

function Container({ 
  children, 
  loginPath, 
  pathOnAuthProblem, 
  allowedRoles,
  insufficientRolePath = '/insufficient-role'
}: AuthContainerProps) {
  const { authenticated, user } = useAuthContext();
  const navigate = useNavigate();

  const [checked, setChecked] = useState(false);

  const checkAuthAndRole = useCallback(async () => {
    if (!authenticated) {
      // const searchParams = new URLSearchParams({
      //   returnTo: window.location.pathname,
      // }).toString();

      // const href = `${loginPath}?${searchParams}`;

      await signInRedirect().catch(async (error) => {
        console.log('Failed to signin redirect');
        await removeUser();
        navigate({ to: pathOnAuthProblem });
      });
    } else {
      // Check roles if specified
      if (allowedRoles && allowedRoles.length > 0) {
        const userRoles = user?.roles?.map((role: Role) => role.name) || [];
        const hasAllowedRole = allowedRoles.some(role => userRoles.includes(role));
        
        if (!hasAllowedRole) {
          navigate({ to: insufficientRolePath });
          return;
        }
      }
      
      setChecked(true);
    }
  }, [authenticated, user, allowedRoles, navigate, pathOnAuthProblem, insufficientRolePath]);

  useEffect(() => {
    checkAuthAndRole();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!checked) {
    return null;
  }

  return <>{children}</>;
}
